/* eslint-disable react/no-unescaped-entities */

import GlobalSeoMetaTags from 'components/GlobalSeoMetaTags/GlobalSeoMetaTags';
import DiscountIcon from 'components/Icons/DiscountIcon';
import InviteIcon from 'components/Icons/InviteIcon';
import ShopIcon from 'components/Icons/ShopIcon';
import UIButton from 'components/Others/UIButton';
import { SeoMetaTags } from 'constants/dataTypes';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { GetStaticProps } from 'next/types';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { openAuth, setInitialAuthTab } from 'redux/features/auth/authSlice';
import { getSeoMetaTags } from 'utils/common.utils';


// Add these constants at the top level
const DISCOUNT_PERCENTAGE = '30';
const DISCOUNT_CODE = 'Welcome30';
const PRICE_BY_REGION = {
  au: '70',
  ca: '70',
  nz: '70',
  international: '41.97'
};

const MULTIFOCAL_PRICE_BY_REGION = {
  au: '175',
  ca: '175',
  nz: '175',
  international: '104.97'
};

export default function ReferralSystem({ seoMetaTags }: { seoMetaTags: SeoMetaTags }) {

  const dispatch = useDispatch();
  const router = useRouter();

  const halfPrice = useMemo(() => {
    const basePrice = PRICE_BY_REGION[router.locale as keyof typeof PRICE_BY_REGION] || '75';
    return basePrice;
  }, [router.locale]);

  const multifocalPrice = useMemo(() => {
    const basePrice = MULTIFOCAL_PRICE_BY_REGION[router.locale as keyof typeof MULTIFOCAL_PRICE_BY_REGION] || '175';
    return basePrice;
  }, [router.locale]);

  const metaValuesByRegion: any = {
    au: {
      title: `Get ${DISCOUNT_PERCENTAGE}% Off Your First Order`,
      description: `Limited time only, glasses from $${halfPrice} includes premium lenses & frames. Register today and get your ${DISCOUNT_PERCENTAGE}% off on your first purchase!`,
    },
    ca: {
      title: `Get ${DISCOUNT_PERCENTAGE}% Off Your First Order`,
      description: `Limited time only, glasses from $${halfPrice} includes premium lenses & frames. Register today and get your ${DISCOUNT_PERCENTAGE}% off on your first purchase!`,
    },
    nz: {
      title: `Get ${DISCOUNT_PERCENTAGE}% Off Your First Order`,
      description: `Limited time only, glasses from $${halfPrice} includes premium lenses & frames. Register today and get your ${DISCOUNT_PERCENTAGE}% off on your first purchase!`,
    },
    international: {
      title: `Get ${DISCOUNT_PERCENTAGE}% Off Your First Order`,
      description: `Limited time only, glasses from $${halfPrice} includes premium lenses & frames. Register today and get your ${DISCOUNT_PERCENTAGE}% off on your first purchase!`,
    }
  }

  const multifocalName = useMemo(() => {
    return (router.locale == 'ca' || router.locale == 'nz') ? 'progressives' : 'multifocals';
  }, [router.locale]);


  return (
    <div>
      <Head>
        <title>{metaValuesByRegion[router.locale as string].title} | Dresden Vision</title>
        <meta name="twitter:title" content={metaValuesByRegion[router.locale as string].title} />
        <meta name="og:title" content={metaValuesByRegion[router.locale as string].title} />
        <meta name="description" content={metaValuesByRegion[router.locale as string].description} />
        <meta property="og:description" content={metaValuesByRegion[router.locale as string].description} />
        <meta name="twitter:description" content={metaValuesByRegion[router.locale as string].description} />
      </Head>

      <div className="text-brand-black100 bg-[#2a2a2a] relative">
        <div
          className="absolute top-0 right-0 w-6/12 h-full hidden md:block"
          style={{
            backgroundImage: `url(${'https://cdn.dresden.vision/images/Welcome30LandingPage.jpg'})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
          role="img"
          aria-label="Welcome 30"
        />
        <div className='md:max-w-[1440px] mx-auto min-h-[800px] relative w-full'>
          <div
            className="w-full h-full block md:hidden md:w-full">
            <Image src='http://cdn.dresden.vision/images/Landing_Page_mobile_ALL.jpg' alt='Welcome 30' width={1440} height={800} />
          </div>
          <div className='flex flex-col justify-between py-8 md:w-6/12 w-full relative z-10'>
            <div>
              <div className='text-left md:text-[66px] text-[38px] font-bold leading-none text-white md:text-left text-center text-[#EFEDAE]'>Time for a change? See better</div>
              <div className='text-[18px] font-semibold mt-[30px] tracking-[-0.24px] text-white md:text-left text-center text-[#EFEDAE] md:block hidden'>30% OFF YOUR FIRST ORDER | PRESCRIPTION GLASSES FROM ${PRICE_BY_REGION[router.locale as keyof typeof PRICE_BY_REGION]}</div>

              <div className='text-[18px] font-semibold mt-[30px] tracking-[-0.24px] text-white md:text-left text-center text-[#EFEDAE] md:hidden'>30% OFF YOUR FIRST ORDER | <br></br> PRESCRIPTION GLASSES FROM ${PRICE_BY_REGION[router.locale as keyof typeof PRICE_BY_REGION]}</div>
              <div className=' mb-[20px]  w-full mt-[25px] tracking-[-0.16px] md:px-0 px-[25px] text-white text-[18px] font-normal md:text-left text-center md:mt-5 text-[#EFEDAE]'>
                <p className='md:block hidden'>
                  Exclusive limited time offer on all prescription and non-prescription glasses!! <br></br>
                  Single vision starting at ${halfPrice}, {multifocalName} from ${multifocalPrice} and much more! Simply<br></br>
                  create your account online, over the phone or in-store and get ordering.
                </p>

                <p className='md:hidden block'>
                  Exclusive limited time offer on all prescription and non-prescription glasses!!
                  Single vision starting at ${halfPrice}, {multifocalName} from ${multifocalPrice} and much more! Simply
                  create your account online, over the phone or in-store and get ordering.
                </p>


                <p className='mt-[20px] md:block hidden'>
                  Select up to 5 pairs, add to cart and use code:
                </p>

                <p className='mt-[20px] md:hidden'>
                  Select up to 5 pairs,<br></br> add to cart and use code:
                </p>
                <p className='mt-[20px] font-bold'>
                  {DISCOUNT_CODE}
                </p>
              </div>
              <div className='flex md:justify-start justify-center'>
                <div
                  className="text-[20px] font-medium leading-[18px] text-[#fff] cursor-pointer"
                  onClick={() => {
                    router.push('/shop?filters=Non+Prescription+Reading+Glasses%2CPrescription+Glasses%2CBlue+Light+Filter+Glasses%2CPrescription+Sunglasses%2CSunglasses%2CReading+Glasses')
                  }}
                >
                  <button className="bg-[#EFEDAE] text-[#2A2A2A] font-semibold md:w-[391px] xsm:w-[320px] py-4 mt-[20px] rounded-[6px] text-black">
                    {`Shop Sale`}
                  </button>
                </div>
              </div>
            </div>
            <div className='text-[13px] text-left text-[#EFEDAE] max-w-[550px] md:mt-10 mt-[40px] mb-12 tracking-[-0.14px] md:px-0 px-[15px] '>
              {(router.locale == 'ca' || router.locale == 'au') && (
                <p>
                  <span className='font-semibold'> T&C: </span>Discount is only available for new customers who haven’t made any previous purchases and is limited to their first purchase only. Discount is limited to 5 pairs only. Discount can only be used once per customer, in one transaction. Discount only available for customers that register an account (no guest checkout) and subscribe to our newsletter. Discount is only available per person and can’t be split between multiple people. Discount is applicable to all glasses only. Discount is not applicable to BYO frame fittings, contact lenses, frames, accessories or optometry services. Offer can’t be used in conjunction with any other campaigns, promotions or discounts. Add items to cart and use code: <span className='font-bold'>WELCOME30</span> Free shipping on online orders only, with a minimum spend of $125. Available in-store, online and over the phone. Limited time only.
                </p>
              )}
              {(router.locale == 'nz') && (
                <p>
                  <span className='font-semibold'> T&C: </span>Discount is only available for new customers who haven’t made any previous purchases and is limited to their first purchase only. Discount is limited to 5 pairs only. Discount can only be used once per customer, in one transaction. Discount only available for customers that register an account (no guest checkout) and subscribe to our newsletter. Discount is only available per person and can’t be split between multiple people. Discount is applicable to all glasses only. Discount is not applicable to BYO frame fittings, contact lenses, frames, accessories or optometry services. Offer can’t be used in conjunction with any other campaigns, promotions or discounts. Add items to cart and use code: <span className='font-bold'>WELCOME30</span> Free shipping on online orders only, with a minimum spend of $125. Available online and over the phone. Limited time only.                </p>
              )}

              {(router.locale == 'international') && (
                <p>
                  <span className='font-semibold'> T&C: </span>Discount is only available for new customers who haven’t made any previous purchases and is limited to their first purchase only. Discount is limited to 5 pairs only. Discount can only be used once per customer, in one transaction. Discount only available for customers that register an account (no guest checkout) and subscribe to our newsletter. Discount is only available per person and can’t be split between multiple people. Discount is applicable to all glasses only. Discount is not applicable to BYO frame fittings, contact lenses, frames, accessories or optometry services. Offer can’t be used in conjunction with any other campaigns, promotions or discounts. Add items to cart and use code: <span className='font-bold'>WELCOME30</span> Free shipping on online orders only, with a minimum spend of $75. Available online and over the phone. Limited time only.                </p>
              )}
            </div>
          </div>

        </div>
      </div>
      <GlobalSeoMetaTags seoMetaTags={seoMetaTags} />
    </div>
  );
}


export const getStaticProps: GetStaticProps = async ({ locale }) => {

  const seoMetaTags = await getSeoMetaTags('/referral-program', locale);

  return {
    props: {
      seoMetaTags
    }
  };
};
